import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { procurementApi, type PurchaseOrderResponse } from '@/api/procurement.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { useSupplierOptions } from '@/hooks/useSupplierOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const GRN_ELIGIBLE = ['APPROVED', 'SENT', 'PARTIALLY_RECEIVED'];

export function PurchaseOrdersPage() {
  const { hasAny } = usePermissions();
  const canCreatePo = hasAny('PROCUREMENT_PO_CREATE', 'PROCUREMENT_MANAGE');
  const canApprovePo = hasAny('PROCUREMENT_PO_APPROVE', 'PROCUREMENT_MANAGE');
  const canReceive = hasAny('PROCUREMENT_RECEIVE', 'PROCUREMENT_MANAGE');
  const { getLabel: getSupplierLabel } = useSupplierOptions();
  const pagination = usePagination();
  const [items, setItems] = useState<PurchaseOrderResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await procurementApi.listPurchaseOrders({
        page: pagination.page,
        size: pagination.size,
      });
      setItems(res.content);
      pagination.applyPageResponse(res);
    } catch (e) {
      showApiError(e, 'Failed to load POs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (id: number, action: 'APPROVE' | 'REJECT') => {
    setBusyId(id);
    try {
      const updated = await procurementApi.approvePurchaseOrder(id, action);
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
      notify.success(action === 'APPROVE' ? 'PO approved' : 'PO rejected');
    } catch (err) {
      showApiError(err, `${action} failed`);
    } finally {
      setBusyId(null);
    }
  };

  const submitPo = async (id: number) => {
    setBusyId(id);
    try {
      const updated = await procurementApi.submitPurchaseOrder(id);
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
      notify.success('PO submitted for approval');
    } catch (err) {
      showApiError(err, 'Submit failed');
    } finally {
      setBusyId(null);
    }
  };

  const createGrn = async (po: PurchaseOrderResponse) => {
    if (!po.items?.length) {
      notify.error('PO has no line items');
      return;
    }
    setBusyId(po.id);
    try {
      const grn = await procurementApi.createGoodsReceipt({
        purchaseOrderId: po.id,
        notes: `GRN from ${po.poNumber}`,
        items: po.items.map((line) => {
          const ordered = Number(line.quantity);
          const received = Number(line.receivedQuantity ?? 0);
          const remaining = Math.max(ordered - received, 0);
          return {
            purchaseOrderItemId: line.id,
            productId: line.productId,
            receivedQuantity: remaining > 0 ? remaining : ordered,
          };
        }).filter((line) => line.receivedQuantity > 0),
      });
      notify.success(`GRN ${grn.grnNumber} created — QC approve then post`);
      await load();
    } catch (err) {
      showApiError(err, 'Create GRN failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Purchase orders"
        description="Approved POs — inventory updates only on GRN post"
        breadcrumbs={[{ label: 'Procurement', to: '/procurement' }, { label: 'Purchase orders' }]}
      />
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">PO number</th>
              <th className="p-3">Status</th>
              <th className="p-3">Supplier</th>
              <th className="p-3">Total</th>
              <th className="p-3">Received</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((po) => (
              <tr key={po.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3 font-medium">{po.poNumber}</td>
                <td className="p-3">{po.poStatus}</td>
                <td className="p-3">{getSupplierLabel(po.supplierId, true)}</td>
                <td className="p-3">{po.totalAmount}</td>
                <td className="p-3">{po.receivedQuantity}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-2">
                    {po.poStatus === 'DRAFT' && canCreatePo && (
                      <Button
                        className="!px-2 !py-1 text-xs"
                        loading={busyId === po.id}
                        onClick={() => void submitPo(po.id)}
                      >
                        Submit
                      </Button>
                    )}
                    {po.poStatus === 'PENDING_APPROVAL' && canApprovePo && (
                      <>
                        <Button
                          className="!px-2 !py-1 text-xs"
                          loading={busyId === po.id}
                          onClick={() => void approve(po.id, 'APPROVE')}
                        >
                          Approve
                        </Button>
                        <Button
                          className="!px-2 !py-1 text-xs"
                          variant="ghost"
                          loading={busyId === po.id}
                          onClick={() => void approve(po.id, 'REJECT')}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {GRN_ELIGIBLE.includes(po.poStatus) && canReceive && (
                      <Button
                        className="!px-2 !py-1 text-xs"
                        variant="secondary"
                        loading={busyId === po.id}
                        onClick={() => void createGrn(po)}
                      >
                        Create GRN
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && !loading && (
          <p className="p-4 text-sm text-slate-500">
            No purchase orders — create from{' '}
            <Link to="/procurement/quotations" className="text-primary-600 hover:underline">
              selected quotation
            </Link>{' '}
            or API.
          </p>
        )}
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={items.length > 0}
        />
      </div>
    </div>
  );
}
